import * as yup from "yup";
import type { Dispatch } from "react";

export const PRICING_TYPES = [
  { value: "flat", label: "Prix fixe" },
  { value: "per_age_group", label: "Par tranche d'âge" },
] as const;

export const FLAT_PRICE_TYPES = [
  { value: "per_seat", label: "Par personne" },
  { value: "per_group", label: "Par groupe" },
] as const;

export const AGE_GROUPS = [
  { value: "infant", label: "Bébé" },
  { value: "child", label: "Enfant" },
  { value: "adult", label: "Adulte" },
  { value: "senior", label: "Senior" },
] as const;

export const WEEKDAYS = [
  { key: "mon", label: "Lun" },
  { key: "tue", label: "Mar" },
  { key: "wed", label: "Mer" },
  { key: "thu", label: "Jeu" },
  { key: "fri", label: "Ven" },
  { key: "sat", label: "Sam" },
  { key: "sun", label: "Dim" },
] as const;

// --- yup schemas ---
const weeklySlotsSchema = yup.object({
  mon: yup.array().of(yup.string().required()).nullable(),
  tue: yup.array().of(yup.string().required()).nullable(),
  wed: yup.array().of(yup.string().required()).nullable(),
  thu: yup.array().of(yup.string().required()).nullable(),
  fri: yup.array().of(yup.string().required()).nullable(),
  sat: yup.array().of(yup.string().required()).nullable(),
  sun: yup.array().of(yup.string().required()).nullable(),
});

const exceptionSchema = yup.object({
  date: yup.string().required("Date requise"),
  slots: yup.array().of(yup.string().required()).nullable(),
});

const ageGroupSchema = yup.object({
  ageGroup: yup
    .string()
    .oneOf(["infant", "child", "adult", "senior"])
    .required("Tranche d'âge requise"),
  endAge: yup
    .number()
    .typeError("Âge requis")
    .min(0)
    .required("Âge max requis"),
  price: yup.number().typeError("Prix requis").min(0).required("Prix requis"),
  priceInEuro: yup
    .number()
    .nullable()
    .transform((value, originalValue) => {
      return originalValue === "" ? null : value;
    }),
  config: yup
    .object({
      isPermitted: yup.boolean().default(true),
      isFree: yup.boolean().nullable(),
      requiresAdult: yup.boolean().nullable(),
    })
    .nullable(),
});

export const scheduleSchema = yup.object({
  name: yup.string().required("Nom requis"),
  startDate: yup.string().required("Date de début requise"),
  endDate: yup
    .string()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .nullable(),
  isActive: yup.boolean().default(true),
  weeklySlots: weeklySlotsSchema,
  pricingType: yup
    .string()
    .oneOf(["flat", "per_age_group"])
    .required("Type de tarification requis"),
  flatPrice: yup
    .number()
    .nullable()
    .transform((value, originalValue) => {
      return originalValue === "" ? null : value;
    })
    .when("pricingType", {
      is: "flat",
      then: (s) => s.required("Prix requis").min(0).typeError("Prix requis"),
      otherwise: (s) => s.nullable(),
    }),
  flatPriceInEuro: yup
    .number()
    .nullable()
    .transform((value, originalValue) => {
      return originalValue === "" ? null : value;
    }),
  flatPriceType: yup
    .string()
    .oneOf(["per_seat", "per_group"])
    .default("per_seat")
    .when("pricingType", {
      is: "flat",
      then: (s) => s.required("Type de prix requis"),
      otherwise: (s) => s.nullable(),
    }),
  minParticipants: yup.number().min(1).default(1),
  maxParticipants: yup
    .number()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .min(1, "Doit être au moins 1")
    .nullable(),
  exceptions: yup.array().of(exceptionSchema).optional(),
  ageGroups: yup
    .array()
    .of(ageGroupSchema)
    .when("pricingType", {
      is: "per_age_group",
      then: (s) => s.min(1, "Au moins une tranche d'âge requise"),
      otherwise: (s) => s.optional(),
    }),
});

const optionSchema = yup.object({
  title: yup.string().required("Titre requis"),
  description: yup.string().nullable(),

  durationValue: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value,
    )
    .typeError("Durée invalide")
    .nullable()
    .min(0),
  durationUnit: yup.string().oneOf(["min", "hr"]).default("min"),

  freeCancelValue: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value,
    )
    .typeError("Valeur invalide")
    .nullable()
    .min(0),
  freeCancelUnit: yup.string().oneOf(["min", "hr"]).default("min"),

  bookingValue: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : value,
    )
    .typeError("Valeur invalide")
    .nullable()
    .min(0),
  bookingThresholdUnit: yup.string().oneOf(["min", "hr"]).default("min"),

  meta: yup
    .object({
      wheelchairAccessible: yup.boolean().nullable(),
      isPrivate: yup.boolean().nullable(),
      isRecommended: yup.boolean().nullable(),
    })
    .nullable(),

  // UI-only discriminator — stripped before hitting the API, see buildFormData
  scheduleMode: yup
    .string()
    .oneOf(["default", "existing", "custom"])
    .default("default"),

  scheduleId: yup
    .string()
    .nullable()
    .when("scheduleMode", {
      is: "existing",
      then: (s) => s.required("Sélectionnez un calendrier"),
      otherwise: (s) => s.nullable().notRequired(),
    }),

  schedule: yup.mixed().when("scheduleMode", {
    is: "custom",
    then: () => scheduleSchema.required("Calendrier requis"),
    otherwise: (s) => s.nullable().notRequired(),
  }),
});

const translationSchema = yup.object({
  lang: yup.string().required("Langue requise"),
  title: yup.string().required("Titre requis"),
  shortDescription: yup.string().nullable(),
  description: yup.string().nullable(),
  includes: yup.string().nullable(),
  notIncludes: yup.string().nullable(),
  imprtMustKnow: yup.string().nullable(),
  imprtMustBring: yup.string().nullable(),
  imprtNotAllowed: yup.string().nullable(),
  notSuitedFor: yup.string().nullable(),
});

export const activitySchema = yup.object({
  title: yup.string().required("Titre requis"),
  status: yup.string().required(),
  placeId: yup.string().required("Établissement requis"),
  isActive: yup.boolean().default(true),
  cityId: yup.object().required("Ce champ est obligatoire"),
  partnerCommission: yup
    .number()
    .min(0, "Min 0%")
    .max(100, "Max 100%")
    .transform((value, original) =>
      original === "" || original === null || original === undefined
        ? 0
        : value,
    )
    .default(0),
  translations: yup
    .array()
    .of(translationSchema)
    .min(1, "Au moins une traduction est requise"),
  itineraries: yup
    .array()
    .of(
      yup.object({
        order: yup.number().required(),
        type: yup
          .string()
          .oneOf(["activity", "transfer"])
          .default("activity")
          .required(),
        title: yup.string().required("Titre de l'étape requis"),
        description: yup.string().nullable(),
        duration: yup.string().nullable(),
        isOptional: yup.boolean().default(false),
      }),
    )
    .optional(),
  pickupDropoff: yup
    .object({
      isPickupAvailable: yup.boolean(),
      isDropoffAvailable: yup.boolean(),
      pickupType: yup.string().nullable(),
      transportation: yup.string().nullable(),
      pickupDescription: yup.string().nullable(),
      pickupGeojson: yup.mixed().nullable(),
      dropoffType: yup.string().nullable(),
      dropoffDescription: yup.string().nullable(),
      dropoffGeojson: yup.mixed().nullable(),
    })
    .optional(),
  schedules: yup.array().of(scheduleSchema).optional(),
  options: yup.array().of(optionSchema).optional(),
  gallery: yup.array().of(yup.string().url()).optional(),
  paymentMethods: yup
    .array()
    .of(yup.string())
    .min(1, "Sélectionnez au moins un moyen de paiement")
    .required("Sélectionnez au moins un moyen de paiement")
    .default([]),
});

export type ActivityFormValues = yup.InferType<typeof activitySchema>;

export const emptySchedule = () => ({
  name: "",
  startDate: "",
  endDate: undefined,
  isActive: true,
  weeklySlots: {},
  pricingType: "flat" as const,
  flatPrice: undefined,
  minParticipants: 1,
  maxParticipants: undefined,
  exceptions: [],
  ageGroups: [],
});

export const emptyOption = () => ({
  title: "",
  description: "",
  durationMinutes: undefined,
  durationUnit: "min",
  freeCancelThreshold: undefined,
  freeCancelThresholdUnit: "min",
  bookingThreshold: undefined,
  bookingThresholdUnit: "min",
  meta: {},
  scheduleMode: "default",
});

export type Option = {
  id?: string;
  title: string;
  description: string;
  durationMinutes?: number;
  durationUnit: "min" | "hr";
  freeCancelThreshold?: number;
  freeCancelThresholdUnit: "min" | "hr";
  bookingThreshold?: number;
  bookingThresholdUnit: "min" | "hr";
  meta?: {
    wheelchairAccessible?: boolean;
    isPrivate?: boolean;
  };
  scheduleMode: "default" | "existing" | "custom";
  scheduleId?: string;
  schedule?: Schedule;
};

export type Schedule = {
  id?: string;
  name: string;
  startDate: string;
  endDate?: string;
  isActive: boolean;
  weeklySlots: {
    mon?: string[];
    tue?: string[];
    wed?: string[];
    thu?: string[];
    fri?: string[];
    sat?: string[];
    sun?: string[];
  };
  pricingType: "flat" | "per_age_group";
  flatPrice?: number;
  minParticipants: number;
  maxParticipants?: number;
  exceptions?: Exception[];
  ageGroups?: AgeGroup[];
};

export type Exception = {
  date: string;
  slots?: string[];
};

export type AgeGroup = {
  ageGroup: "infant" | "child" | "adult" | "senior";
  endAge: number;
  price: number;
  config?: {
    isPermitted?: boolean;
    isFree?: boolean;
    requiresAdult?: boolean;
  };
};

export type CityPlace = {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  address?: string;
};

export type WizardStage =
  | "type"
  | "activityPick"
  | "transferPick"
  | "location"
  | "duration"
  | "optional";

export type StepDraft = {
  type: "" | "activity" | "transfer";
  /** Multi-selected/typed activity tags — merged into `description`/`activityType` on save. */
  title: string;
  selectedActivities: string[];
  /** Free-text transfer mode, e.g. "Van", "Minibus", "À pied". */
  transferMode: string;
  durationHours: string;
  durationMinutes: string;
  isOptional: boolean;
  city: string;
  placeId: string;
  address: string;
  latitude?: number;
  longitude?: number;
  placeName?: string;
};

export type ActivityBookingView = {
  name: string;
  image: string | null;
  option: string;
  participants: string;
};

export type ActivityBookingFields = {
  pickupLocation?: {
    latitude?: number;
    longitude?: number;
    address?: string;
    placeName?: string;
  } | null;
  pickupLocationUnknown?: boolean;
  activity?: {
    id: string;
    title?: string;
    galleries?: { id: string; url: string; order: number }[];
  } | null;
  activityOptionBookings?: {
    id: string;
    numberOfReservation: number;
    unitPrice?: string;
    flatPriceInEuro?: string;
    ageGroupSnapshot?: {
      label?: string;
      price?: string;
      priceInEuro?: string;
    } | null;
    option?: { id: string; title?: string } | null;
  }[];
};



export type WeekDay =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

export type DayStatus = "closed" | "openAllDay" | "custom";
export type CopyTarget = "workDays" | "weekends" | "all";
export type TabKey = 1 | 2;

export interface TimeRange {
  from: string;
  to: string;
}

export interface DayFormValue {
  status?: DayStatus;
  ranges?: TimeRange[];
}

export interface ExceptionFormValue {
  name: string;
  date: string;
  repeating: string | boolean;
}

export type OpeningHoursFormValues = Partial<Record<WeekDay, DayFormValue>> & {
  exceptions?: ExceptionFormValue[];
};

export interface ApiDayHours {
  closed?: boolean;
  openAllDay?: boolean;
  ranges?: TimeRange[];
}

export type ApiOpeningHours = Partial<Record<WeekDay, ApiDayHours>> & {
  exceptions?: ExceptionFormValue[];
};

export type CleanedOpeningHours = Record<WeekDay, ApiDayHours> & {
  exceptions: ExceptionFormValue[];
};

export interface OpeningHoursModalProps {
  visible: boolean;
  onClose: () => void;
  placeId?: string;
}






export type ActiveModal =
  | "delete"
  | "menu"
  | "hours"
  | "more"
  | "qr"
  | "password"
  | null;

export interface PlacesTableProps {
  places: Place[];
  placesToBeDeletedList: Dispatch<BulkDeleteActions>;
}

export interface PlaceRowProps {
  place: Place;
  deletedList: Dispatch<BulkDeleteActions>;
}